import React, { useState, useEffect, useRef } from 'react'
import { StyledComponent } from 'styled-components'

// contract to say that this component needs a styledComponent TD
// and a styledComponent Input
export interface EditableCellStyledComponentsType {
  EditableTd: StyledComponent<'td', {}>
  EditableInput: StyledComponent<'input', {}>
}

interface EditableTableCellProps {
  value: string
  startInEditMode: boolean
  isValidSubmission: Function
  validKeysRegExp: RegExp
  inputProperties: any
  editableComponents: EditableCellStyledComponentsType
  submit: Function
  justify: 'start' | 'center' | 'end'
  // structure of row is dependent on table it is being used in
  row: any
}
/**
 * Reusable EditableTableCell component
 * @param {value} string original value for cell
 * @param {startInEditMode} boolean when true, cell will start in edit mode
 * @param {isValidSubmission} Function func to check if the value is valid, and can be 'submitted'
 * @param {validKeysRegExp} RegExp regex of valid keys for this input
 * @param {inputProperties} any object of any key-value pair that html INPUT element can take, (ie, autoFocus, maxLength, onFocus...)
 * @param {editableComponents} EditableComponents consists of both a styled component <td> and <input>
 * @param {submit} Function handler function for when new entry is submitted
 * @param {justify} string determines alignment of text inside editable cell
 * @param {row} object all values for this row in the table.
 */
export const EditableTableCell: React.FC<EditableTableCellProps> = ({
  value: originalValue,
  startInEditMode,
  isValidSubmission,
  validKeysRegExp,
  inputProperties,
  submit,
  editableComponents: { EditableTd, EditableInput },
  justify,
  row,
}) => {
  // When isBeingEdited is true, the table cell will become an input
  const [isBeingEdited, setIsBeingEdited] = useState(startInEditMode)
  // updated Value tracks the new user inputted value
  const originalValRef = useRef<string>(originalValue)
  const [updatedValue, setNewVal] = useState(originalValRef.current)
  // ref to table cell so we can watch for outside clicks
  const wrapperRef = useRef<HTMLTableDataCellElement>(null)

  useEffect(() => {
    // We only want this effect, when we are NOT starting in editMode.
    // If we are starting in editMode, this component will act as a permanent
    // input element. And we do not want to turn back into a non-editable component
    // when the mouse is clicked outside of this TD element.
    if (!startInEditMode) {
      const handleOutsideClick = (event: any) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
          reset()
        }
      }
      document.addEventListener('mousedown', handleOutsideClick)
      return () => document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  function onKeyPressHandler(e: any) {
    // 'Enter' submits, if valid
    if (e.which === 13 && isValidSubmission(updatedValue)) {
      const thisRowInfo = {
        newValue: updatedValue,
        oldValue: originalValRef.current,
        di_code: row.di_code,
        institution: row.school_name,
      }
      submit(thisRowInfo, reset, triggerUpdateValue)
      setIsBeingEdited(false)
      // 'Escape' resets
    } else if (e.which === 27) {
      reset()
      // if NOT a valid key, reject
    } else if (!validKeysRegExp.test(e.key)) {
      e.preventDefault()
    }
  }

  // Escape key not triggered by onKeyPress
  // watch for escape key and rest if clicked
  function onKeyDownHandler(e: any) {
    if (e.which === 27) {
      reset()
    }
  }

  function triggerUpdateValue(newVal: string) {
    originalValRef.current = newVal
  }

  // get out of edit mode, go back to orig. val.
  function reset() {
    setNewVal(originalValRef.current)
    setIsBeingEdited(false)
  }

  return isBeingEdited ? (
    <EditableTd ref={wrapperRef}>
      <div className={`display-flex justify-content-${justify}`}>
        <EditableInput
          type="text"
          {...inputProperties}
          value={updatedValue}
          onKeyPress={onKeyPressHandler}
          onKeyDown={onKeyDownHandler}
          onChange={(e: any) => setNewVal(e.target.value)}
        />
      </div>
    </EditableTd>
  ) : (
    <td onDoubleClick={() => setIsBeingEdited(true)}>
      <div className={`display-flex justify-content-${justify}`}>{updatedValue}</div>
    </td>
  )
}
